import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { logError, getUserFriendlyMessage } from '@/utils/errorTracking';

/**
 * ErrorPage: Route-level error handler
 * Used when an error occurs during route rendering or data fetching
 *
 * Usage: Can be set as error element in route config
 * <Route path="/orders/:id" element={<OrderPage />} errorElement={<ErrorPage />} />
 *
 * Or navigate to it manually:
 * navigate('/error', { state: { error: new Error('Custom error'), retry: () => {} } });
 */

interface ErrorPageState {
  error?: Error;
  retry?: () => void;
}

export const ErrorPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isRetrying, setIsRetrying] = useState(false);

  const state = location.state as ErrorPageState | null;
  const error = state?.error || new Error('An unexpected error occurred');
  const isDev = import.meta.env.DEV;
  const userMessage = getUserFriendlyMessage(error);

  useEffect(() => {
    // Log error when page loads
    logError(error, {
      component: 'ErrorPage',
      action: 'Route Error',
    });
  }, [error]);

  const handleRetry = async () => {
    if (state?.retry) {
      setIsRetrying(true);
      try {
        await state.retry();
        // If retry succeeds, navigate back
        navigate(-1);
      } catch (err) {
        logError(err, { component: 'ErrorPage', action: 'Retry Failed' });
      } finally {
        setIsRetrying(false);
      }
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-lg p-8 space-y-6">
          {/* Error Icon */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4v2m0 4v2M12 4a8 8 0 100 16 8 8 0 000-16z"
                />
              </svg>
            </div>
          </div>

          {/* Error Title & Message */}
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold text-gray-900">Oops!</h1>
            <p className="text-gray-600 text-sm">{userMessage}</p>
          </div>

          {/* Dev-only Error Details */}
          {isDev && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
              <div>
                <p className="text-xs font-semibold text-red-900 mb-1">Error Details:</p>
                <p className="text-xs font-mono text-red-700 break-words">{error.message}</p>
              </div>

              {error.stack && (
                <details className="mt-2">
                  <summary className="text-xs font-semibold text-red-800 cursor-pointer hover:text-red-900">
                    View Stack Trace
                  </summary>
                  <pre className="mt-2 text-xs font-mono text-red-600 bg-red-900 bg-opacity-5 p-2 rounded overflow-auto max-h-32 whitespace-pre-wrap break-words">
                    {error.stack}
                  </pre>
                </details>
              )}

              <div className="text-xs text-red-700 pt-2 border-t border-red-200">
                <p className="font-semibold mb-1">Debug Info:</p>
                <p>URL: {location.pathname}</p>
                <p>Timestamp: {new Date().toISOString()}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3 pt-2">
            {state?.retry && (
              <button
                onClick={handleRetry}
                disabled={isRetrying}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium text-sm"
              >
                {isRetrying ? 'Retrying...' : 'Try Again'}
              </button>
            )}

            <button
              onClick={handleGoBack}
              className="w-full px-4 py-2 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 transition font-medium text-sm"
            >
              Go Back
            </button>

            <button
              onClick={handleDashboard}
              className="w-full px-4 py-2 text-blue-600 hover:text-blue-700 font-medium text-sm underline"
            >
              Back to Dashboard
            </button>
          </div>

          {/* Support Info */}
          <div className="text-center pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Need help?{' '}
              <a href="mailto:support@printdeck.direct" className="text-blue-600 hover:underline">
                Contact support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
